import React, { useEffect, useMemo, useState } from "react";
import axios from "../../../api/axios";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import PageShell, { SectionCard } from "components/patch/PageShell";

export default function StaffList() {
  const [staff, setStaff] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchStaff();
  }, []);

  async function fetchStaff() {
    setLoading(true);
    try {
      const response = await axios.get("/staff");
      setStaff(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching staff:", error);
      setStaff([]);
    } finally {
      setLoading(false);
    }
  }

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditOpen = (staffMember) => {
    setSelectedStaff(staffMember);
    setFormData({
      surname: staffMember.biodata?.surname || "",
      firstname: staffMember.biodata?.firstname || "",
      othername: staffMember.biodata?.othername || "",
      title: staffMember.biodata?.title?.name || "",
      gender: staffMember.biodata?.gender?.name || "",
      employment_status: staffMember.employment_status || "",
      grade_level: staffMember.grade_level || "",
      step: staffMember.step || "",
    });
    setOpenEdit(true);
  };

  const handleEditClose = () => {
    setOpenEdit(false);
    setSelectedStaff(null);
    setFormData({});
  };

  const handleFormChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleUpdate = async () => {
    if (!selectedStaff?.pfno) {
      return;
    }

    setSaving(true);

    try {
      const pfno = selectedStaff.pfno;
      await axios.put(`/staff/${pfno}`, formData);

      setStaff((prev) =>
        prev.map((s) =>
          s.pfno === pfno
            ? {
                ...s,
                employment_status: formData.employment_status,
                grade_level: formData.grade_level,
                step: formData.step,
                biodata: {
                  ...s.biodata,
                  surname: formData.surname,
                  firstname: formData.firstname,
                  othername: formData.othername,
                  title: {
                    ...(s.biodata?.title || {}),
                    name: formData.title,
                  },
                  gender: {
                    ...(s.biodata?.gender || {}),
                    name: formData.gender,
                  },
                },
              }
            : s
        )
      );

      handleEditClose();
    } catch (error) {
      console.error("Update failed:", error);
    } finally {
      setSaving(false);
    }
  };

  const filteredStaff = useMemo(() => {
    return [...staff]
      .filter((s) => {
        const searchText = search.toLowerCase();

        return (
          s.pfno?.toLowerCase().includes(searchText) ||
          s.biodata?.surname?.toLowerCase().includes(searchText) ||
          s.biodata?.firstname?.toLowerCase().includes(searchText) ||
          s.biodata?.othername?.toLowerCase().includes(searchText)
        );
      })
      .sort((a, b) => {
        const aMissing = [a.biodata?.surname, a.biodata?.firstname].filter(
          (v) => !v || v === "-"
        ).length;
        const bMissing = [b.biodata?.surname, b.biodata?.firstname].filter(
          (v) => !v || v === "-"
        ).length;
        return aMissing - bMissing;
      });
  }, [staff, search]);

  return (
    <PageShell
      title="Edit Staff"
      subtitle="Search employee records and update staff information from one workspace."
    >
      <SectionCard
        title="Search and Review"
        subtitle="Locate staff quickly by PF number or employee name."
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <MDInput
              label="Search by PF No, surname, firstname, or othername"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <MDButton
              variant="gradient"
              color="error"
              fullWidth
              onClick={fetchStaff}
              disabled={loading}
            >
              {loading ? "Refreshing..." : "Refresh Records"}
            </MDButton>
          </Grid>
        </Grid>
      </SectionCard>

      <Box mt={3}>
        <SectionCard
          title="Staff Records"
          subtitle={`${filteredStaff.length.toLocaleString()} record(s) found`}
        >
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: "18px",
              border: "1px solid #f1eaea",
              boxShadow: "none",
              overflow: "hidden",
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>S/N</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>PF No</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Surname</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Firstname</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Othername</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Employment Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Grade Level</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Step</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredStaff.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography sx={{ color: "#62708a", fontSize: "0.95rem" }}>
                        No staff records found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredStaff
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((s, index) => (
                      <TableRow key={s.pfno} hover>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>{s.pfno || "-"}</TableCell>
                        <TableCell>{s.biodata?.surname || "-"}</TableCell>
                        <TableCell>{s.biodata?.firstname || "-"}</TableCell>
                        <TableCell>{s.biodata?.othername || "-"}</TableCell>
                        <TableCell>{s.employment_status || "-"}</TableCell>
                        <TableCell>{s.grade_level || "-"}</TableCell>
                        <TableCell>{s.step || "-"}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Edit Staff">
                            <IconButton onClick={() => handleEditOpen(s)} color="error">
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredStaff.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </SectionCard>
      </Box>

      <Dialog open={openEdit} onClose={handleEditClose} maxWidth="md" fullWidth>
        <DialogTitle>Edit Staff Details</DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <MDInput
                label="Surname"
                name="surname"
                fullWidth
                value={formData.surname || ""}
                onChange={handleFormChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <MDInput
                label="Firstname"
                name="firstname"
                fullWidth
                value={formData.firstname || ""}
                onChange={handleFormChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <MDInput
                label="Othername"
                name="othername"
                fullWidth
                value={formData.othername || ""}
                onChange={handleFormChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <MDInput
                label="Title"
                name="title"
                fullWidth
                value={formData.title || ""}
                onChange={handleFormChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <MDInput
                label="Gender"
                name="gender"
                fullWidth
                value={formData.gender || ""}
                onChange={handleFormChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <MDInput
                label="Employment Status"
                name="employment_status"
                fullWidth
                value={formData.employment_status || ""}
                onChange={handleFormChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <MDInput
                label="Grade Level"
                name="grade_level"
                fullWidth
                value={formData.grade_level || ""}
                onChange={handleFormChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <MDInput
                label="Step"
                name="step"
                fullWidth
                value={formData.step || ""}
                onChange={handleFormChange}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <MDButton variant="outlined" color="dark" onClick={handleEditClose}>
            Cancel
          </MDButton>

          <MDButton variant="gradient" color="error" onClick={handleUpdate} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
